import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPublicExhibitorDetail } from "../../../../services/exhibitor";
import { submitInquiry } from "../../../../services/inquiry";
import { getFavorites, toggleFavorite } from "../../../../services/favorites";
import { toast } from "react-toastify";
import moment from "moment";
import { Users, Play, FileText, LayoutGrid, Globe } from "lucide-react";
import { useSite } from "../../../../store/useSite";
import MaterialViewer from "../../../siteExhibitors/boothMaterialsTab/MaterialViewer";
import { Header1 } from "../../comp/themes/modern/home/header/Header1";

// Sub-components
import ProfileBanner from "./ProfileBanner";
import TabNavigation from "./TabNavigation";
import MediaGallery from "./MediaGallery";
import ConnectivitySidebar from "./ConnectivitySidebar";
import TeamSection from "./TeamSection";
import InquirySidebarForm from "./InquirySidebarForm";
import MemberProfileSidebar from "./MemberProfileSidebar";
import BookAppointmentSidebar from "./BookAppointmentSidebar";
import BookingSuccessModal from "./BookingSuccessModal";

const ExhibitorProfileSkeleton = () => (
  <div className="min-h-screen flex flex-col bg-[#f9f9f9]">
    <Header1 />
    <div className="w-full">
      {/* Banner Skeleton */}
      <div className="w-full h-52 md:h-72 bg-slate-200 animate-pulse relative" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-20 relative z-10 pb-12">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm flex flex-col md:flex-row gap-6 items-start">
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-slate-200/80 animate-pulse border-4 border-white shadow-md flex-shrink-0" />
          <div className="space-y-3 flex-1 w-full pt-2">
            <div className="h-7 w-60 bg-slate-200/80 rounded animate-pulse" />
            <div className="h-4 w-40 bg-slate-100 rounded animate-pulse" />
            <div className="h-4 w-full max-w-md bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
        {/* Tab Bar & Content Skeleton */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="h-12 w-full bg-white rounded-xl border border-gray-100 p-2 flex gap-2">
              <div className="h-8 w-24 bg-slate-200/80 rounded-lg animate-pulse" />
              <div className="h-8 w-24 bg-slate-100 rounded-lg animate-pulse" />
              <div className="h-8 w-24 bg-slate-100 rounded-lg animate-pulse" />
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3 shadow-sm">
              <div className="h-5 w-48 bg-slate-200/80 rounded animate-pulse" />
              <div className="h-4 w-full bg-slate-100 rounded animate-pulse" />
              <div className="h-4 w-5/6 bg-slate-100 rounded animate-pulse" />
              <div className="h-4 w-3/4 bg-slate-100 rounded animate-pulse" />
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-3 shadow-sm">
              <div className="h-5 w-36 bg-slate-200/80 rounded animate-pulse" />
              <div className="h-10 w-full bg-slate-100 rounded-xl animate-pulse" />
              <div className="h-10 w-full bg-slate-100 rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function SiteExhibitorProfilePage() {
  const { exhibitor_id } = useParams();
  const navigate = useNavigate();

  // Basic safety check for HTML content
  const sanitizeHtml = (html) => {
    if (!html) return "";
    // Remove script tags and their content
    return html.replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gim, "");
  };
  const { siteUser, eventData } = useSite();
  const [activeTab, setActiveTab] = useState("all");
  const [exhibitor, setExhibitor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isInquirySidebarOpen, setIsInquirySidebarOpen] = useState(false);
  const [inquiryData, setInquiryData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    designation: "",
    organization: "",
    message: "",
  });

  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [bookingMember, setBookingMember] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    if (siteUser) {
      setInquiryData((prev) => ({
        ...prev,
        firstName: siteUser.name || "",
        lastName: siteUser.last_name || "",
        email: siteUser.email || "",
      }));
    }
  }, [siteUser]);

  useEffect(() => {
    const fetchExhibitorDetail = async () => {
      try {
        setLoading(true);
        const response = await getPublicExhibitorDetail(exhibitor_id);
        if (response.data.success === true) {
          setExhibitor(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching exhibitor detail:", error);
      } finally {
        setLoading(false);
      }
    };

    if (exhibitor_id) {
      fetchExhibitorDetail();
    }
  }, [exhibitor_id]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!exhibitor_id || !siteUser) return;
      try {
        const response = await getFavorites(
          exhibitor.event_id || eventData?.id,
        );
        if (response.data.success) {
          const isFav = response.data.data.some(
            (fav) => String(fav.id) === String(exhibitor_id),
          );
          setIsFavorited(isFav);
        }
      } catch (error) {
        console.error("Error checking favorite status:", error);
      }
    };

    if (exhibitor && siteUser) {
      checkFavoriteStatus();
    }
  }, [exhibitor, siteUser]);

  const handleFavoriteToggle = async () => {
    if (!siteUser) {
      toast.info("Please sign in to manage your list");
      return;
    }

    try {
      setFavLoading(true);
      const response = await toggleFavorite(exhibitor.event_id, exhibitor_id);
      if (response.data.success) {
        setIsFavorited(!isFavorited);
        toast.success(response.data.data?.message || response.data.message);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorites");
    } finally {
      setFavLoading(false);
    }
  };

  const handleMemberClick = (member) => {
    setCurrentMember(member);
    setIsSidebarOpen(true);
  };

  const handleBookMeeting = (member) => {
    if (!siteUser) {
      toast.info("Please sign in to book a meeting");
      return;
    }
    setBookingMember(member);
    setIsBookingOpen(true);
  };

  const handleBookingSuccess = (details) => {
    setBookingDetails(details);
    setIsSuccessOpen(true);
  };

  const handleView = (material) => {
    setSelectedMaterial(material);
    setIsViewerOpen(true);
  };

  const handleInquiryChange = (e) => {
    const { name, value } = e.target;
    setInquiryData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        exhibitor_id,
        event_id: exhibitor.event_id,
        attendee_name:
          `${inquiryData.firstName} ${inquiryData.lastName}`.trim(),
        attendee_email: inquiryData.email,
        attendee_phone: inquiryData.phone,
        company_name: inquiryData.organization,
        subject: `Inquiry for ${exhibitor.name}`,
        message: inquiryData.message,
      };

      const res = await submitInquiry(payload);
      if (res.data.success) {
        toast.success("Inquiry submitted successfully!");
        setIsInquirySidebarOpen(false);
        setInquiryData({
          firstName: siteUser?.name || "",
          lastName: siteUser?.last_name || "",
          email: siteUser?.email || "",
          phone: "",
          designation: "",
          organization: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error(error.response?.data?.message || "Failed to submit inquiry.");
    }
  };

  if (loading) {
    return <ExhibitorProfileSkeleton />;
  }

  if (!exhibitor) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f9f9f9]">
        <Header1 />
        <div className="flex-1 flex items-center justify-center text-gray-500">
          Exhibitor not found.
        </div>
      </div>
    );
  }

  const isEvent =
    eventData?.start_date && eventData?.end_date
      ? moment().isBetween(
          moment(eventData.start_date),
          moment(eventData.end_date),
          "day",
          "[]",
        )
      : false;

  const features = exhibitor.category_features || [];

  const hasFeature = (key) => {
    const feature = features.find((f) => f.key === key);
    return feature ? feature.enabled === 1 : false;
  };

  const showVideos = hasFeature("videos");
  const showCollateral = hasFeature("collateral");
  const showInquiries =
    hasFeature("inquiries") && siteUser?.exhibitor?.id !== exhibitor?.id;
  const showPromoBanner = hasFeature("promotional_banner");

  const getYoutubeId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url?.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const media = (exhibitor.media || []).map((m) => {
    let thumb = m.thumbnail;
    if (!thumb && m.media_type === "video") {
      const ytId = getYoutubeId(m.url || m.file_url);
      if (ytId) thumb = `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`;
    }
    return {
      ...m,
      url: m.url || m.file_url,
      thumbnail: thumb,
    };
  });

  const videos = media.filter((m) => m.media_type === "video");
  const collateral = media.filter((m) => m.media_type === "collateral");
  const promoBanners = exhibitor.promoBanners || [];

  const hasPromo = showPromoBanner && promoBanners.length > 0;

  const tabs = [
    { key: "all", label: "All", icon: <LayoutGrid className="w-4 h-4" /> },
    ...(exhibitor.company_description
      ? [
          {
            key: "overview",
            label: "Overview",
            icon: <Globe className="w-4 h-4" />,
          },
        ]
      : []),
    ...(showVideos && videos.length > 0
      ? [{ key: "videos", label: "Videos", icon: <Play className="w-4 h-4" /> }]
      : []),
    ...(showCollateral && collateral.length > 0
      ? [
          {
            key: "collateral",
            label: "Collateral",
            icon: <FileText className="w-4 h-4" />,
          },
        ]
      : []),
    ...(exhibitor.members?.length > 0 && siteUser
      ? [
          {
            key: "booth_member",
            label: "Booth Member",
            icon: <Users className="w-4 h-4" />,
          },
        ]
      : []),
  ];

  return (
    <div
      className={`text-slate-900 font-sans min-h-screen transition-all duration-300`}
    >
      <Header1 />
      <div
        className={`grid grid-cols-1 md:grid-cols-3 gap-x-10 items-start ${hasPromo ? "max-w-7xl" : "max-w-4xl"} mx-auto`}
      >
        {/* Main Column */}
        <div
          className={`${hasPromo ? "md:col-span-2" : "md:col-span-3"} h-fit border-x border-gray-300`}
        >
          <ProfileBanner
            exhibitor={exhibitor}
            showInquiries={showInquiries}
            onInquiryClick={() => setIsInquirySidebarOpen(true)}
            hasPromo={hasPromo}
            isFavorited={isFavorited}
            isFavLoading={favLoading}
            onFavoriteToggle={handleFavoriteToggle}
          />

          {/* Mobile Promo Banner: Only shows on mobile if exists, below banner info */}
          {hasPromo && (
            <div className="block md:hidden px-4 py-3">
              <ConnectivitySidebar
                promoBanners={promoBanners}
                showPromoBanner={showPromoBanner}
              />
            </div>
          )}

          {/* Content Section (Tabs/Navigation) */}
          <div className="border-t border-gray-100 px-7">
            <div className="relative">
              <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />

              <div className="mt-8 pb-32">
                <div className="space-y-12">
                  {/* Overview Section */}
                  {(activeTab === "all" || activeTab === "overview") &&
                    exhibitor.company_overview && (
                      <section className="bg-white p-6 sm:p-10 rounded-3xl shadow-sm border border-slate-100 transition-all duration-300">
                        <div className="flex items-center gap-3 mb-8">
                          <div className="w-1.5 h-8 bg-indigo-600 rounded-full" />
                          <h2 className="text-xl font-semibold text-slate-900">
                            Company Overview
                          </h2>
                        </div>
                        <div
                          className="text-slate-600 leading-relaxed text-base sm:text-lg font-normal prose prose-indigo max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(exhibitor.company_overview),
                          }}
                        />
                      </section>
                    )}

                  {siteUser && (
                    <TeamSection
                      members={exhibitor.members}
                      activeTab={activeTab}
                      onMemberClick={handleMemberClick}
                      onBookMeeting={handleBookMeeting}
                      isEvent={
                        isEvent && siteUser?.exhibitor?.id !== exhibitor?.id
                      }
                    />
                  )}
                  <MediaGallery
                    videos={videos}
                    collateral={collateral}
                    activeTab={activeTab}
                    showVideos={showVideos}
                    showCollateral={showCollateral}
                    onView={handleView}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Column: Only shows on desktop if hasPromo is true */}
        {hasPromo && (
          <div className="hidden md:block sticky top-17 self-start col-span-1 space-y-8 pt-0">
            <ConnectivitySidebar
              promoBanners={promoBanners}
              showPromoBanner={showPromoBanner}
            />
          </div>
        )}
      </div>

      <MemberProfileSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        member={currentMember}
      />

      <MaterialViewer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        material={selectedMaterial}
      />

      <InquirySidebarForm
        isOpen={isInquirySidebarOpen}
        onClose={() => setIsInquirySidebarOpen(false)}
        data={inquiryData}
        onChange={handleInquiryChange}
        onSubmit={handleInquirySubmit}
        siteUser={siteUser}
      />

      <BookAppointmentSidebar
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        member={bookingMember}
        exhibitorId={exhibitor_id}
        eventData={{ ...eventData, exhibitor_name: exhibitor.name }} // Merging for context
        siteUser={siteUser}
        onBookingSuccess={handleBookingSuccess}
      />

      <BookingSuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
        bookingDetails={bookingDetails}
      />
    </div>
  );
}
